/**
 * SEO component that queries for data with
 * Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/how-to/querying-data/use-static-query/
 */

import React, { type FC, type ReactElement } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useI18next } from "gatsby-plugin-react-i18next";

import Languages from "@data/languages.json";
import { Helmet } from "@dr.pogodin/react-helmet";
import type Language from "@interfaces/Language";

const Seo: FC<{
  title?: string;
  description?: string;
  keywords?: string;
  lang?: string;
  image?: string;
  robots?: string;
  children?: any;
}> = ({
  title,
  description,
  keywords,
  lang,
  image,
  robots,
  children,
}): ReactElement => {
  const { t, originalPath } = useI18next();
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          og_image
          siteUrl
        }
      }
    }
  `);
  const languageKeys: string[] = [];
  Languages.filter((lang: Language) => lang.enabled).forEach(
    (lang: Language, j: number) => {
      languageKeys.push(lang.id);
    },
  );
  const siteUrl = process.env.GATSBY_SITE_URL || site.siteMetadata.siteUrl;

  const baseUrl = siteUrl.endsWith("/") ? siteUrl : `${siteUrl}/`;
  const langPath = originalPath.startsWith("/")
    ? originalPath.slice(1)
    : originalPath;

  const metaUrlWithLang: Record<string, string> = {};
  languageKeys.forEach((langKey: string) => {
    const prefix = langKey === "en" ? "" : `${langKey}/`;
    metaUrlWithLang[langKey] = `${baseUrl}${prefix}${langPath}`;
  });

  const metaImage = image
    ? `${siteUrl}${image}`
    : `${siteUrl}${site.siteMetadata.og_image}`;

  const ogTitleKey = "og_title";
  const ogDescriptionKey = "og_description";
  const targetTitle =
    title ||
    (t(ogTitleKey) && t(ogTitleKey) !== ogTitleKey ? t(ogTitleKey) : null) ||
    site.siteMetadata.title;
  const targetDescription =
    description ||
    (t(ogDescriptionKey) && t(ogDescriptionKey) !== ogDescriptionKey
      ? t(ogDescriptionKey)
      : null) ||
    site.siteMetadata.description;

  const defaultKeywords =
    "eSIM, buy eSIM, data eSIM, global eSIM, regional eSIM, shop eSIM, e-SIM, eSIM marketplace, Traveler, data plan, connectivity, purchase local eSIM, buy e-SIM for international travel, world best travel esim, world best travel sim provider, cheap data, affordable global eSIM plans, best data plans for travelers 2025, 5G-enabled eSIM plans, download eSIM profile, eSIM for IOT, best esim provider, multi-country eSIM, travel esim, affordable eSIM, reliable eSIM, global coverage, best travel sim award winner, Ciao, Local Plan eSIM, Regional Plan eSIM";

  return (
    <Helmet>
      <html
        lang={lang}
        dir={
          Languages.find((l: Language) => l.id === lang)?.is_rtl ? "rtl" : "ltr"
        }
      />
      <title>{targetTitle}</title>
      <meta name="description" content={targetDescription} />
      <meta property="og:title" content={targetTitle} />
      <meta property="og:description" content={targetDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={metaUrlWithLang[lang ?? "en"]} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={targetTitle} />
      <meta name="twitter:image" content={metaImage} />
      <meta name="twitter:description" content={targetDescription} />
      <meta
        name="google-site-verification"
        content={process.env.GATSBY_GOOGLE_SITE_VERIFICATION}
      />
      {Object.keys(metaUrlWithLang).map(key => (
        <link
          key={key}
          rel="alternate"
          // The site's Korean locale id is "kr" (a country code); hreflang
          // needs the ISO 639-1 language code "ko" or Google ignores it.
          hrefLang={key === "kr" ? "ko" : key}
          href={metaUrlWithLang[key]}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={metaUrlWithLang.en} />
      {robots != null ? <meta name="robots" content={robots} /> : null}
      <meta name="keywords" content={keywords || defaultKeywords} />
      <link rel="canonical" href={metaUrlWithLang[lang ?? "en"]} />
      {children}
    </Helmet>
  );
};

export default Seo;
